/**
 * The customer's own order history.
 *
 * Scoped by RLS to the anonymous session on this device, which is the honest
 * limit of what the app can offer without asking for an account: clearing site
 * data loses the history, but the six-character code always recovers an
 * individual order.
 */

import { Pressable } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDayTime, formatMoney, totalItemCount, type Order } from '@yummy/shared';
import { fetchMyOrders } from '@yummy/db';
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingState,
  MetaRow,
  Row,
  Screen,
  Stack,
  StatusChip,
  ToneBadge,
  Text,
  useAsync,
  useT,
} from '@yummy/ui-rnr';

import { AppBar } from '../components/AppBar';
import { useBackTo } from '../app/navigation';
import { supabase } from '../lib/supabase';
import { ReceiptText } from 'lucide-react-native';

/**
 * Clearance between the floating header and the first order card, in points.
 *
 * The content is inset by exactly the header's height, which left the list flush
 * against the bar. A card carries its own border and corners, so it needs a hair
 * of clearance rather than the room a bare line of text does — what the eye
 * reads as the gap is mostly the bar's own 9pt of slack under its controls.
 */
const HEADER_GAP = 2.5;

/**
 * Lists this device's orders and the two per-device preferences.
 *
 * @returns The history screen.
 */
export function HistoryScreen() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Where this list was opened from, forwarded to the order it opens: a menu
   * behind the list is what lets that order step back to it.
   */
  const listFrom = (location.state as { from?: string } | null)?.from;
  // Opened from the menu, the checkout, an order, or the landing page, this
  // goes back to whichever of them it was; only a direct open falls back.
  const goBack = useBackTo('/');
  const orders = useAsync<Order[]>(() => fetchMyOrders(supabase, 30), []);

  if (orders.initial) {
    return (
      <Screen>
        <LoadingState message={t('customer.history.loading')} />
      </Screen>
    );
  }

  if (orders.error) {
    return (
      <Screen>
        <ErrorBanner message={orders.error} onRetry={orders.reload} />
      </Screen>
    );
  }

  const list = orders.data ?? [];

  return (
    <Screen
      autoHideHeader
      header={<AppBar title={t('customer.history.title')} onBack={goBack} />}
      headerGap={HEADER_GAP}>
      <Stack gap="lg">
        {list.length === 0 ? (
          <EmptyState
            icon={ReceiptText}
            title={t('customer.history.empty')}
            message={t('customer.history.emptyHint')}
            actionLabel={t('customer.history.enterCode')}
            onAction={() => navigate('/')}
          />
        ) : (
          <Stack gap="md">
            {list.map((order) => (
              <Pressable
                key={order.id}
                // Says where the order was opened from — and, through `listFrom`,
                // whether a menu is behind this list — so its controls can step
                // back rather than stack another copy of a screen.
                onPress={() =>
                  navigate(`/order/${order.id}`, { state: { from: 'orders', listFrom } })
                }
                accessibilityRole="button"
                className="active:opacity-70">
                <Card className="p-4">
                  <Stack gap="sm">
                    <Row spread gap="md" align="center">
                      <Text numberOfLines={1} className="flex-1 text-lg font-semibold">
                        {order.restaurant_name}
                      </Text>
                      {/* The default size, which is what the tracking card's
                          chip uses: at `sm` this was 11pt where the same chip
                          on the order screen was 12. The money rides beside it
                          when the counter took it, the same fact in the same
                          place as on the order itself. */}
                      <Row gap="sm" align="center">
                        {order.paid_at ? (
                          <ToneBadge label={t('customer.order.paid')} tone="success" />
                        ) : null}
                        <StatusChip status={order.status} />
                      </Row>
                    </Row>

                    <MetaRow
                      items={[
                        `#${order.code}`,
                        t('common.itemCount', { count: totalItemCount(order) }),
                        formatMoney(order.total_cents, order.currency),
                      ]}
                    />

                    <Text className="text-subtle-foreground text-sm">
                      {t(order.is_scheduled ? 'customer.history.forScheduled' : 'customer.history.placedAt', {
                        when: formatDayTime(
                          order.scheduled_for ?? order.placed_at,
                          order.timezone,
                          undefined,
                          t,
                        ),
                      })}
                    </Text>
                  </Stack>
                </Card>
              </Pressable>
            ))}
          </Stack>
        )}
      </Stack>
    </Screen>
  );
}
